import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, X, ChevronLeft, ChevronRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Seo, breadcrumb } from '../components/Seo';
import { mockData } from '../mock';
import { useT } from '../context/LanguageContext';
import { api, pictureUrl } from '../lib/api';
import { SubmitPhotoButton } from '../components/SubmitPhoto';

const Gallery = () => {
  const { t } = useT();
  const [index, setIndex] = useState(null);
  const [extra, setExtra] = useState([]);
  useEffect(() => { api.get('/gallery').then((r) => setExtra(r.data.map((p) => ({ ...p, src: pictureUrl(p.src) })))).catch(() => {}); }, []);
  const photos = [...extra, ...mockData.gallery];

  useEffect(() => {
    if (index === null) return;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') setIndex(null);
      if (e.key === 'ArrowRight') setIndex((i) => (i + 1) % photos.length);
      if (e.key === 'ArrowLeft') setIndex((i) => (i - 1 + photos.length) % photos.length);
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [index, photos.length]);

  return (
    <div className="bg-black min-h-screen text-white" data-testid="gallery-page">
      <Seo title="Galleri | Refuel Weekend 2027" description="Sjå bilete frå tidlegare Refuel Weekend – lovsong, aktivitetar, paintball og stemning i Hjeltefjorden Arena." path="/galleri" jsonLd={breadcrumb([{ name: 'Refuel Weekend', path: '/' }, { name: 'Galleri', path: '/galleri' }])} />
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-12 md:py-16">
        <Link to="/" data-testid="gallery-back-link" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/50 border border-white/15 text-white hover:bg-black/70 transition-colors text-sm mb-8"><ArrowLeft size={16} /> {t('Tilbake')}</Link>
        <p className="text-[#ff2e2e] font-bold uppercase tracking-widest text-sm mb-2">{t('Galleri')}</p>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" style={{ fontFamily: "'Oktah Round', sans-serif" }}>{t('Sjå bilete frå tidlegare Refuel')}</h1>
          <SubmitPhotoButton className="self-start px-6 py-3 rounded-full border-2 border-[#ff2e2e] text-[#ff2e2e] font-bold hover:bg-[#ff2e2e] hover:text-black transition-colors" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {photos.map((ph, i) => (
            <button key={ph.id || ph.src} type="button" onClick={() => setIndex(i)} data-testid={`gallery-item-${i}`} className={`group relative ${ph.portrait ? "aspect-[3/2] sm:row-span-2 sm:aspect-auto" : "aspect-[3/2]"} rounded-3xl overflow-hidden bg-[#1a1424] border border-white/5 text-left`}>
              <img src={ph.src} alt={ph.alt} loading="lazy" decoding="async" width={ph.portrait ? 1333 : 2000} height={ph.portrait ? 2000 : 1333} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <p className="absolute bottom-4 left-5 right-5 text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity">{ph.alt}</p>
            </button>
          ))}
        </div>
      </main>
      <Footer />

      {index !== null && (
        <div role="dialog" aria-modal="true" aria-label={photos[index].alt} className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4" onClick={() => setIndex(null)} data-testid="gallery-lightbox">
          <button type="button" aria-label="Lukk" onClick={() => setIndex(null)} data-testid="gallery-lightbox-close" className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"><X size={20} /></button>
          <button type="button" aria-label="Førre" onClick={(e) => { e.stopPropagation(); setIndex((index - 1 + photos.length) % photos.length); }} data-testid="gallery-lightbox-prev" className="absolute left-3 md:left-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"><ChevronLeft size={22} /></button>
          <img src={photos[index].src} alt={photos[index].alt} onClick={(e) => e.stopPropagation()} className="max-h-[85vh] max-w-full rounded-2xl shadow-2xl" />
          <button type="button" aria-label="Neste" onClick={(e) => { e.stopPropagation(); setIndex((index + 1) % photos.length); }} data-testid="gallery-lightbox-next" className="absolute right-3 md:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"><ChevronRight size={22} /></button>
          <p className="absolute bottom-6 left-0 right-0 text-center text-white/60 text-sm">{photos[index].alt} · {index + 1}/{photos.length}</p>
        </div>
      )}
    </div>
  );
};

export default Gallery;
